import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api, apiUrl } from '@/lib/api'
import { fmtDate } from '@/lib/utils'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { QueryError } from '@/components/ui/QueryError'
import { X } from 'lucide-react'
import type { CareerApplication, FormSchema } from '@/types/careers'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
  EmptyState,
  LoadingRows,
} from '@/components/ui/DataTable'

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'muted'> = {
  received: 'default',
  requested_info: 'warning',
  accepted: 'success',
  denied: 'danger',
}

function AnswerList({ answers, schema }: { answers: Record<string, string>; schema?: FormSchema }) {
  const sections = schema?.sections || []
  if (!sections.length) {
    return (
      <dl className="space-y-3 text-sm">
        {Object.entries(answers).map(([k, v]) => (
          <div key={k}>
            <dt className="text-[#9aabbb] text-xs uppercase tracking-wide">{k}</dt>
            <dd className="text-[#f0f4f8] mt-1 whitespace-pre-wrap">{v || '—'}</dd>
          </div>
        ))}
      </dl>
    )
  }

  return (
    <div className="space-y-6">
      {sections.map((sec) => {
        const qs = sec.questions.filter((q) => answers[q.id] !== undefined || q.type === 'file')
        if (!qs.length) return null
        return (
          <div key={sec.id}>
            <h4 className="text-sm font-semibold text-[#40dbf1] mb-3">{sec.title}</h4>
            <dl className="space-y-3 text-sm">
              {qs.map((q) => (
                <div key={q.id}>
                  <dt className="text-[#9aabbb]">{q.label}</dt>
                  <dd className="text-[#f0f4f8] mt-1 whitespace-pre-wrap">
                    {q.type === 'file' ? (answers[q.id] ? 'File uploaded' : '—') : answers[q.id] || '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )
      })}
    </div>
  )
}

export function ApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('ref'))
  const [respondMsg, setRespondMsg] = useState('')
  const [respondError, setRespondError] = useState('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-applications', statusFilter],
    queryFn: () => {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : ''
      return api<{ applications: CareerApplication[] }>(`/api/admin/applications${q}`)
    },
  })

  const apps = data?.applications || []

  const { data: detailData, isLoading: detailLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ['admin-application', selectedId],
    queryFn: () => api<{ application: CareerApplication }>(`/api/admin/applications/${selectedId}`),
    enabled: !!selectedId,
  })

  const detail = detailData?.application

  const respondMutation = useMutation({
    mutationFn: async ({ action }: { action: 'accept' | 'deny' | 'request' }) => {
      if (!selectedId) throw new Error('No application selected')
      return api(`/api/admin/applications/${selectedId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action, message: respondMsg }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-applications'] })
      qc.invalidateQueries({ queryKey: ['admin-application', selectedId] })
      setRespondMsg('')
      setRespondError('')
    },
    onError: (e: Error) => setRespondError(e.message),
  })

  function selectApp(app: CareerApplication) {
    setSelectedId(app.ref)
    setSearchParams({ ref: app.ref })
    setRespondMsg('')
    setRespondError('')
  }

  function closeDetail() {
    setSelectedId(null)
    setSearchParams({})
    setRespondMsg('')
    setRespondError('')
  }

  return (
    <div>
      <PageHeader
        title="Career applications"
        description="Review submissions from the public careers page. Respond to candidates — they'll receive an email confirmation."
      />

      <div className="admin-filter-tabs" role="tablist" aria-label="Application status">
        {['', 'received', 'requested_info', 'accepted', 'denied'].map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => setStatusFilter(s)}
            role="tab"
            aria-selected={statusFilter === s}
            className={`admin-filter-tab ${
              statusFilter === s
                ? 'is-active'
                : ''
            }`}
          >
            {s ? s.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      <div className="admin-review-layout">
        <div>
          {error ? (
            <QueryError title="Couldn’t load applications" error={error} onRetry={() => void refetch()} />
          ) : (
            <DataTable>
              <table className="w-full min-w-[560px]">
                <DataTableHead>
                  <DataTableHeaderCell>Applicant</DataTableHeaderCell>
                  <DataTableHeaderCell>Role</DataTableHeaderCell>
                  <DataTableHeaderCell>Status</DataTableHeaderCell>
                  <DataTableHeaderCell>Submitted</DataTableHeaderCell>
                </DataTableHead>
                {isLoading ? (
                  <LoadingRows cols={4} />
                ) : apps.length === 0 ? (
                  <tbody>
                    <tr>
                      <td colSpan={4}>
                        <EmptyState
                          title="No applications yet"
                          description="Applications appear when candidates submit the built-in careers form."
                        />
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <DataTableBody>
                    {apps.map((a) => (
                      <DataTableRow
                        key={a.ref}
                        className={selectedId === a.ref ? 'bg-[rgba(64,219,241,0.06)]' : 'cursor-pointer'}
                        onClick={() => selectApp(a)}
                      >
                        <DataTableCell>
                          <div className="font-medium text-[#f0f4f8]">{a.applicantName}</div>
                          <div className="text-xs text-[#9aabbb]">{a.applicantEmail}</div>
                        </DataTableCell>
                        <DataTableCell className="text-[#9aabbb] text-sm">{a.postingTitle || '—'}</DataTableCell>
                        <DataTableCell>
                          <Badge variant={STATUS_VARIANT[a.status] || 'muted'}>
                            {a.status.replace(/_/g, ' ')}
                          </Badge>
                        </DataTableCell>
                        <DataTableCell className="text-[#9aabbb] text-xs">{fmtDate(a.createdAt)}</DataTableCell>
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                )}
              </table>
            </DataTable>
          )}
        </div>

        <aside className="admin-review-detail">
          {!selectedId ? (
            <div className="admin-detail-empty"><span>←</span><strong>Select an application</strong><p>Its answers, attachments, and response controls will appear here.</p></div>
          ) : detailLoading ? (
            <p className="text-sm text-[#9aabbb]">Loading…</p>
          ) : detailError ? (
            <QueryError title="Couldn’t load this application" error={detailError} onRetry={() => void refetchDetail()} />
          ) : detail ? (
            <div className="space-y-5">
              <div className="admin-detail-heading">
                <div>
                  <h3 className="text-lg font-semibold text-[#f0f4f8]">{detail.applicantName}</h3>
                  <p className="text-sm text-[#9aabbb]">{detail.applicantEmail}</p>
                </div>
                <button type="button" onClick={closeDetail} aria-label="Close application"><X aria-hidden="true" /></button>
              </div>
              <div>
                <p className="text-sm text-[#40dbf1] mt-1">{detail.postingTitle}</p>
                <Badge className="mt-2" variant={STATUS_VARIANT[detail.status] || 'muted'}>
                  {detail.status.replace(/_/g, ' ')}
                </Badge>
              </div>

              <AnswerList answers={detail.answers} schema={detail.formSchema} />

              <AttachmentList applicationRef={detail.ref} />

              {detail.staffMessage && (
                <div className="text-sm rounded-lg bg-[rgba(255,255,255,0.04)] p-3">
                  <p className="text-xs text-[#9aabbb] mb-1">Staff message</p>
                  <p className="text-[#f0f4f8] whitespace-pre-wrap">{detail.staffMessage}</p>
                </div>
              )}

              <div className="space-y-3 pt-2 border-t border-[rgba(255,255,255,0.08)]">
                <textarea
                  className="w-full min-h-[80px] rounded-[10px] border border-[rgba(255,255,255,0.1)] bg-[rgba(6,9,12,0.6)] px-3 py-2 text-sm text-[#f0f4f8]"
                  placeholder="Optional message to include in the email…"
                  value={respondMsg}
                  onChange={(e) => setRespondMsg(e.target.value)}
                />
                {respondError && <p className="text-xs text-[#fca5a5]">{respondError}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => respondMutation.mutate({ action: 'accept' })} disabled={respondMutation.isPending}>
                    Accept
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => respondMutation.mutate({ action: 'request' })} disabled={respondMutation.isPending}>
                    Request info
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => respondMutation.mutate({ action: 'deny' })} disabled={respondMutation.isPending}>
                    Decline
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#fca5a5]">Application not found.</p>
          )}
        </aside>
      </div>
    </div>
  )
}

function AttachmentList({ applicationRef }: { applicationRef: string }) {
  const { data } = useQuery({
    queryKey: ['career-attachments', applicationRef],
    queryFn: () => api<{ files: { type: string; filename: string }[] }>(`/api/careers/attachments?ref=${encodeURIComponent(applicationRef)}`),
    enabled: !!applicationRef,
  })

  const files = data?.files || []
  if (!files.length) return null


  return (
    <div>
      <h4 className="text-xs text-[#9aabbb] uppercase tracking-wide mb-2">Uploaded files</h4>
      <ul className="space-y-1 text-sm">
        {files.map((f) => (
          <li key={f.filename}>
            <a
              className="text-[#40dbf1] hover:underline"
              href={apiUrl(`/api/careers/attachments/download?ref=${encodeURIComponent(applicationRef)}&filename=${encodeURIComponent(f.filename)}`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {f.type}: {f.filename}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
